/**
 * usePersons - Cadastro único de Pessoas (fornecedor, fabricante, prestador...)
 *
 * Fonte 100% server-side via RPCs `get_persons`, `upsert_person`,
 * `check_person_document`, `upsert_person_role` e `deactivate_person_role`.
 * Independente do cadastro de Clientes (crm_customers não é alterado).
 *
 * RELEASE: 2026-10-06 v1.0.0 - Versão inicial (Fase 1 do módulo Estoque).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('usePersons');

/** Papéis aceitos (espelha a allowlist da RPC). */
export const PERSON_ROLES = [
  { value: 'supplier', label: 'Fornecedor', newLabel: 'Novo fornecedor' },
  { value: 'manufacturer', label: 'Fabricante', newLabel: 'Novo fabricante' },
  { value: 'service_provider', label: 'Prestador de serviço', newLabel: 'Novo prestador' },
  { value: 'carrier', label: 'Transportadora', newLabel: 'Nova transportadora' },
  { value: 'other', label: 'Outro' },
] as const;

export type PersonRole = (typeof PERSON_ROLES)[number]['value'];

/**
 * Submenus especialistas do menu Pessoas (2026-10-06).
 * slug = trecho da rota /tenant/persons/:role; menuKey = permissão própria do submenu.
 */
export const PERSON_ROLE_SECTIONS: { slug: string; role: PersonRole; menuKey: string; title: string; label: string; newLabel: string }[] = [
  { slug: 'fornecedores', role: 'supplier', menuKey: 'persons_suppliers', title: 'Fornecedores', label: 'Fornecedor', newLabel: 'Novo fornecedor' },
  { slug: 'fabricantes', role: 'manufacturer', menuKey: 'persons_manufacturers', title: 'Fabricantes', label: 'Fabricante', newLabel: 'Novo fabricante' },
  { slug: 'prestadores', role: 'service_provider', menuKey: 'persons_providers', title: 'Prestadores', label: 'Prestador de serviço', newLabel: 'Novo prestador' },
  { slug: 'transportadoras', role: 'carrier', menuKey: 'persons_carriers', title: 'Transportadoras', label: 'Transportadora', newLabel: 'Nova transportadora' },
];

export interface Person {
  id: string;
  person_type: 'PF' | 'PJ';
  document: string | null;
  name: string;
  legal_name: string | null;
  trade_name: string | null;
  email: string | null;
  phone: string | null;
  cep: string | null;
  street: string | null;
  address_number: string | null;
  complement: string | null;
  district: string | null;
  city: string | null;
  state: string | null;
  notes: string | null;
  linked_customer_id: string | null;
  is_active: boolean;
  roles: PersonRole[];
  provider_details: { user_id: string | null; service_area: string | null } | null;
  supplier_details: { state_registration: string | null; payment_term_days: number | null; purchase_notes: string | null } | null;
}

export interface PersonsFilters {
  search: string;
  role: PersonRole | 'all';
  status: 'active' | 'inactive' | 'all';
  page: number;
  pageSize: number;
}

/** Resultado da checagem de documento já existente. */
export interface PersonDocumentCheck {
  person: { id: string; name: string; is_active: boolean; roles: PersonRole[] } | null;
  customer: { id: string; name: string } | null;
}

// Mantém o `this` do cliente ao chamar RPCs fora da tipagem gerada
const rpc = (name: string, args: Record<string, unknown>) =>
  (supabase.rpc as unknown as (n: string, a: Record<string, unknown>) => Promise<{ data: unknown; error: { message: string } | null }>)
    .call(supabase, name, args);

/** Lista paginada de pessoas. */
export function usePersonsList(filters: PersonsFilters, enabled = true) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: ['persons', tenantId, filters],
    enabled: !!tenantId && enabled,
    placeholderData: (prev) => prev,
    queryFn: async () => {
      const { data, error } = await rpc('get_persons', {
        p_search: filters.search || null,
        p_role: filters.role === 'all' ? null : filters.role,
        p_status: filters.status,
        p_page: filters.page,
        p_page_size: filters.pageSize,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) {
        logger.error('Erro ao listar pessoas', { error: error.message });
        throw new Error(error.message);
      }
      const payload = (data || {}) as { total?: number; items?: Person[] };
      return { total: payload.total ?? 0, items: payload.items ?? [] };
    },
  });
}

/** Mutações do cadastro de pessoas. */
export function usePersonsMutations() {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['persons', tenantId] });

  /** Verifica se o CPF/CNPJ já existe em Pessoas ou em Clientes. */
  const checkDocument = async (document: string): Promise<PersonDocumentCheck | null> => {
    const digits = document.replace(/\D/g, '');
    if (digits.length !== 11 && digits.length !== 14) return null;
    const { data, error } = await rpc('check_person_document', { p_document: digits, p_tenant_hint: tenantId ?? null });
    if (error) {
      logger.warn('Falha ao checar documento', { error: error.message });
      return null;
    }
    return data as PersonDocumentCheck;
  };

  const upsertPerson = useMutation({
    mutationFn: async ({ id, data }: { id: string | null; data: Record<string, unknown> }) => {
      const { data: result, error } = await rpc('upsert_person', {
        p_data: data,
        p_person_id: id,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) throw new Error(error.message);
      return result as string;
    },
    onSuccess: () => {
      invalidate();
      toast.success('Pessoa salva');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  /** Acrescenta um papel a uma pessoa existente (evita cadastro duplicado). */
  const addRole = useMutation({
    mutationFn: async ({ personId, role }: { personId: string; role: PersonRole }) => {
      const { error } = await rpc('upsert_person_role', { p_person_id: personId, p_role: role, p_tenant_hint: tenantId ?? null });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      invalidate();
      toast.success('Papel acrescentado ao cadastro existente');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return { checkDocument, upsertPerson, addRole };
}
